import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { X, Upload, Trash2, ExternalLink, FolderOpen, FileText, Image as ImageIcon, Film, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "jpg", "jpeg", "png", "webp", "heic", "gif", "mp4", "mov", "avi", "mkv", "webm"];
const MAX_MB = 100;

const humanSize = (n) => {
  const x = Number(n) || 0;
  if (x < 1024) return `${x} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  return `${(x / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (mime, name = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if ((mime || "").startsWith("image/") || ["jpg", "jpeg", "png", "webp", "heic", "gif"].includes(ext)) return <ImageIcon size={14} />;
  if ((mime || "").startsWith("video/") || ["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return <Film size={14} />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <FileSpreadsheet size={14} />;
  return <FileText size={14} />;
};

const OrderDocsModal = ({ order, onClose }) => {
  const [data, setData] = useState({ connected: true, folder_id: "", folder_link: "", files: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadInfo, setUploadInfo] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/orders/${order.id}/drive/files`);
      setData(r.data || { connected: false, files: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar file");
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => { load(); }, [load]);

  const doUploadMany = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const valid = [];
    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) { toast.error(`"${file.name}": tipe .${ext} tidak diizinkan — dilewati`); continue; }
      if (file.size > MAX_MB * 1024 * 1024) { toast.error(`"${file.name}": terlalu besar (max ${MAX_MB}MB) — dilewati`); continue; }
      valid.push(file);
    }
    if (valid.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < valid.length; i++) {
      const file = valid[i];
      setProgress(0);
      setUploadInfo(`File ${i + 1}/${valid.length}: ${file.name}`);
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/orders/${order.id}/drive/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
        });
        ok++;
      } catch (e) {
        fail++;
        toast.error(`"${file.name}": ${e?.response?.data?.detail || e?.message || "gagal upload"}`);
      }
    }
    setUploading(false); setProgress(0); setUploadInfo("");
    if (ok > 0) toast.success(fail > 0 ? `${ok} file ter-upload, ${fail} gagal` : `${ok} file ter-upload ke Drive`);
    await load();
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) doUploadMany(e.dataTransfer.files);
  };

  const removeFile = async (f) => {
    if (!window.confirm(`Hapus file "${f.name}" dari Drive? Tidak bisa di-undo.`)) return;
    try {
      await api.delete(`/orders/${order.id}/drive/files/${f.id}`);
      toast.success("File dihapus");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="order-docs-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="label-cap">Dokumentasi Order</div>
            <div className="font-heading text-xl font-black tracking-tight">{order.order_no}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F4F4F5]" data-testid="docs-close-button"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {!data.connected && (
            <div className="border border-[#D97706]/30 bg-[#D97706]/5 text-[#D97706] text-xs p-3" data-testid="docs-not-connected">
              Google Drive belum terhubung. Hubungkan dulu di halaman <b>Pengaturan</b>.
            </div>
          )}

          {data.connected && (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="docs-dropzone"
                className={`border-2 border-dashed cursor-pointer transition-colors p-6 text-center ${dragOver ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#E4E4E7] bg-[#F4F4F5] hover:bg-[#E4E4E7]/30"}`}
              >
                <Upload size={28} className="mx-auto mb-2 text-[#52525B]" />
                <div className="text-sm font-bold">Klik atau drag beberapa file sekaligus ke sini</div>
                <div className="text-xs text-[#52525B] mt-1">Bisa pilih banyak file · PDF, Doc, Excel, Foto, Video · maks {MAX_MB}MB/file</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  data-testid="docs-file-input"
                  accept={ALLOWED_EXT.map(e => `.${e}`).join(",")}
                  onChange={(e) => { if (e.target.files?.length) doUploadMany(e.target.files); e.target.value = ""; }}
                />
              </div>
              {uploading && (
                <div className="text-xs text-[#52525B]" data-testid="docs-upload-progress">
                  Mengunggah… {uploadInfo} — {progress}%
                  <div className="h-1.5 bg-[#E4E4E7] mt-1 overflow-hidden">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}

              {data.folder_link && (
                <a href={data.folder_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs text-[#002FA7] hover:underline" data-testid="docs-open-folder">
                  <FolderOpen size={14} /> Buka folder di Drive
                </a>
              )}

              <div className="border border-[#E4E4E7]">
                <div className="px-3 py-2 bg-[#F4F4F5] label-cap flex items-center justify-between">
                  <span>File ({data.files?.length || 0})</span>
                </div>
                {loading ? (
                  <div className="p-4 text-xs text-[#52525B]">Memuat…</div>
                ) : (data.files || []).length === 0 ? (
                  <div className="p-4 text-xs text-[#52525B]" data-testid="docs-empty">Belum ada dokumentasi untuk order ini.</div>
                ) : (
                  <ul className="divide-y divide-[#E4E4E7]" data-testid="docs-file-list">
                    {data.files.map((f) => (
                      <li key={f.id} className="flex items-center gap-3 p-3" data-testid={`docs-file-${f.id}`}>
                        {f.thumbnailLink ? (
                          <img src={f.thumbnailLink} alt={f.name} className="h-10 w-10 object-cover border border-[#E4E4E7]" />
                        ) : (
                          <div className="h-10 w-10 flex items-center justify-center bg-[#F4F4F5] border border-[#E4E4E7] text-[#52525B]">{iconFor(f.mimeType, f.name)}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold truncate">{f.name}</div>
                          <div className="text-xs text-[#52525B] font-mono">{humanSize(f.size)} · {(f.createdTime || "").slice(0, 10)}</div>
                        </div>
                        {f.webViewLink && (
                          <a href={f.webViewLink} target="_blank" rel="noreferrer" title="Buka di Drive" className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><ExternalLink size={14} /></a>
                        )}
                        <button onClick={() => removeFile(f)} title="Hapus" className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDocsModal;
